import { useMemo } from "react";
import { RotateCcw } from "lucide-react";
import { Badge, KlasseBadge } from "./ui";
import { InfoTip } from "./Erklaerung";
import { useApp } from "../../store/AppStore";
import { contractStore, useContract } from "../../store/contract";
import { contractComparison } from "../../domain/contractCompare";
import { formatFue, formatFue1, formatNum, formatWeight } from "../../domain/rules";

// ============================================================
//  Vertragsgrundlage je Klasse — dieselbe Tabelle in den Einstellungen und auf der Vertragsseite,
//  derselbe Store. Eingabe: angenommene Personen je Klasse. Daneben „heute" aus der aktiven
//  Identitätsauflösung und die Differenz in Personen und FUE. Summe = Vertragszahl, Summe der
//  Differenz = Delta des Cockpits.
// ============================================================

export function VertragsgrundlageHinweis() {
  return <InfoTip>Annahme, bis der Vertrag vorliegt; Summe ist überall die Vertragszahl. „Heute" sind die unter dem aktiven Identitätsschlüssel erkannten Personen je Klasse; die Differenz je Klasse ist in Personen und in FUE (Personen × Gewicht) angegeben. Die Summenzeile der FUE-Differenz ist das Delta des Cockpits.</InfoTip>;
}

/** Schalter rechts im Kartenkopf: „Standardwerte" als Badge, sonst Zurücksetzen. */
export function VertragsgrundlageZuruecksetzen() {
  const { ratio } = useApp();
  const { isDefault } = useContract(ratio);
  return isDefault ? (
    <Badge tone="outline">Standardwerte</Badge>
  ) : (
    <button
      type="button"
      onClick={() => contractStore.reset()}
      className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium"
      style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--color-brand-600)" }}
    >
      <RotateCcw size={14} /> Standardwerte
    </button>
  );
}

const signed = (n: number) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${formatNum(Math.abs(n))}`;
const signed1 = (n: number) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${formatFue1(Math.abs(n))}`;

/**
 * Eingabetabelle. `compact` lässt Gewicht und FUE-Beitrag weg (Vertragsseite) — die Eingabefelder
 * bleiben dieselben.
 */
export function VertragsgrundlageTabelle({ compact = false }: { compact?: boolean }) {
  const { ratio, identity, kpis } = useApp();
  const { basis } = useContract(ratio);
  const cmp = useMemo(() => contractComparison(basis, identity.byClass, ratio), [basis, identity, ratio]);
  const th = (h: string, i: number) => (
    <th key={h} className={`text-faint py-2 text-xs font-semibold tracking-wide uppercase ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
  );
  const heads = compact
    ? ["Lizenzklasse", "vertraglich angenommen (Personen)", "heute (Personen)", "Differenz (Personen)", "FUE-Differenz"]
    : ["Lizenzklasse", "Anzahl (Personen)", "Gewicht", "FUE-Beitrag", "heute (Personen)", "heute (FUE)", "Differenz (Personen)", "Differenz (FUE)"];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-soft">{heads.map(th)}</tr></thead>
        <tbody>
          {cmp.rows.map((r) => (
            <tr key={r.cls} className="border-b border-soft last:border-0">
              <td className="py-2.5"><KlasseBadge cls={r.cls} /></td>
              <td className="py-2.5 text-right">
                <input
                  type="number"
                  step={1}
                  min={0}
                  value={r.assumed}
                  aria-label={`Vertraglich angenommene Personen ${r.cls}`}
                  onChange={(e) => { const v = e.target.valueAsNumber; contractStore.set(r.cls, Number.isFinite(v) ? v : 0); }}
                  className="tnum bg-card w-28 rounded-md px-2 py-1 text-right text-sm"
                  style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-strong)" }}
                />
              </td>
              {!compact && <td className="tnum text-faint py-2.5 text-right">{formatWeight(r.weight)}</td>}
              {!compact && <td className="tnum text-normal py-2.5 text-right font-medium">{formatFue1(r.assumedFue)}</td>}
              <td className="tnum text-dim py-2.5 text-right">{formatNum(r.today)}</td>
              {!compact && <td className="tnum text-dim py-2.5 text-right">{formatFue1(r.todayFue)}</td>}
              <td className="tnum py-2.5 text-right" style={{ color: r.diffPersons > 0 ? "var(--color-amp-red)" : "var(--text-dim)" }}>{signed(r.diffPersons)}</td>
              <td className="tnum py-2.5 text-right font-medium" style={{ color: r.diffFue > 0 ? "var(--color-amp-red)" : "var(--text-dim)" }}>{signed1(r.diffFue)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="text-strong py-2.5 font-semibold">Summe</td>
            <td className="tnum text-strong py-2.5 text-right font-semibold">{formatNum(cmp.assumed)}</td>
            {!compact && <td />}
            {!compact && <td className="tnum py-2.5 text-right font-semibold" style={{ color: "var(--color-brand-600)" }}>{formatFue(cmp.assumedFue)}</td>}
            <td className="tnum text-strong py-2.5 text-right font-semibold">{formatNum(cmp.today)}</td>
            {!compact && <td className="tnum py-2.5 text-right font-semibold" style={{ color: "var(--color-amp-red)" }}>{formatFue(cmp.todayFue)}</td>}
            <td className="tnum text-strong py-2.5 text-right font-semibold">{signed(cmp.diffPersons)}</td>
            <td className="tnum py-2.5 text-right font-semibold" style={{ color: cmp.deltaCockpit > 0 ? "var(--color-amp-red)" : "var(--text-strong)" }}>{signed(cmp.deltaCockpit)} FUE</td>
          </tr>
        </tfoot>
      </table>
      <div className="text-faint mt-2 text-xs">
        Vertragszahl {formatFue(cmp.assumedFue)} · Bedarf heute {formatFue(kpis.ist)} · Delta {signed(kpis.delta)} FUE (Cockpit)
      </div>
    </div>
  );
}
